import { differenceInCalendarDays, differenceInHours, isAfter } from 'date-fns';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'shared/components/ui/icon';
import { useToday } from 'shared/contexts/today';
import { Timestamp } from 'shared/types/timestamp';

import * as Styled from './time-left.style';

export type TimeLeftProps = {
  startIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  date: Timestamp;
  isShort?: boolean;
  isMidShort?: boolean;
  isSmallIcon?: boolean;
  noColor?: boolean;
};

export const TimeLeft: React.FC<TimeLeftProps> = ({
  date,
  startIcon,
  isShort,
  isMidShort,
  isSmallIcon,
  noColor,
}) => {
  const containerRef = useRef<HTMLSpanElement>(null);
  const today = useToday();
  const { t } = useTranslation();
  const isOverdue = isAfter(today, date);
  const hoursLeft = differenceInHours(date, today);
  const daysLeft = isOverdue
    ? differenceInCalendarDays(date, today)
    : Math.ceil(hoursLeft / 24);
  const yearsLeftCount = daysLeft / 365;
  const yearsLeft =
    yearsLeftCount % 1 === 0
      ? Math.floor(yearsLeftCount)
      : yearsLeftCount.toFixed(1);
  const monthsLeft = Math.floor(daysLeft / 30);
  const weeksLeft = Math.floor(daysLeft / 7);

  const overdueLabel = isShort
    ? 'general.labels.days-ago.short'
    : isMidShort
      ? 'general.labels.days-ago.mid-short'
      : 'general.labels.days-ago';
  const yearsLeftLabel = isShort
    ? 'general.labels.years-left.short.title'
    : isMidShort
      ? 'general.labels.years-left.mid-short.title'
      : 'general.labels.years-left.title';
  const monthsLeftLabel = isShort
    ? 'general.labels.months-left.short.title'
    : isMidShort
      ? 'general.labels.months-left.mid-short.title'
      : 'general.labels.months-left.title';
  const weeksLeftLabel = isShort
    ? 'general.labels.weeks-left.short.title'
    : isMidShort
      ? 'general.labels.weeks-left.mid-short.title'
      : 'general.labels.weeks-left.title';
  const daysLeftLabel = isShort
    ? 'general.labels.days-left.short.title'
    : isMidShort
      ? 'general.labels.days-left.mid-short.title'
      : 'general.labels.days-left.title';

  const isLastWeek = daysLeft <= 7;

  return (
    <Styled.Container
      ref={containerRef}
      $overdue={isOverdue}
      $lastWeek={isLastWeek}
      $noColor={!!noColor}
    >
      {!!startIcon && (
        <Styled.IconContainer $isSmall={!!isSmallIcon}>
          <Icon icon={startIcon} />
        </Styled.IconContainer>
      )}

      <Styled.Label>
        {t(
          isOverdue
            ? overdueLabel
            : daysLeft >= 365
              ? yearsLeftLabel
              : daysLeft >= 30
                ? monthsLeftLabel
                : daysLeft >= 15
                  ? weeksLeftLabel
                  : daysLeftLabel,
          {
            amount: isOverdue
              ? daysLeft
              : daysLeft >= 365
                ? yearsLeft
                : daysLeft >= 30
                  ? monthsLeft
                  : daysLeft >= 15
                    ? weeksLeft
                    : daysLeft,
            count: isOverdue
              ? daysLeft
              : daysLeft >= 365
                ? yearsLeftCount
                : daysLeft >= 30
                  ? monthsLeft
                  : daysLeft >= 15
                    ? weeksLeft
                    : daysLeft,
          },
        )}
      </Styled.Label>
    </Styled.Container>
  );
};
